import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useBrainDump } from "@/components/BrainDumpProvider";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Target,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Volume2,
  VolumeX,
  Calendar,
  Sparkles,
  MapPin,
  Coffee,
  Zap,
  Phone,
  Bell,
  Settings,
  Save,
  Flame,
  ArrowRight,
  X,
  Moon,
  Brain,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MorningBriefing from "@/components/MorningBriefing";
import EndOfDayCheckout from "@/components/EndOfDayCheckout";
import DailyWisdomCard from "@/components/DailyWisdomCard";
import TomorrowLookAhead from "@/components/TomorrowLookAhead";
import DailyBrief from "@/components/DailyBrief";
import MiddayCheckpoint from "@/components/MiddayCheckpoint";
import StaleTasksCard from "@/components/StaleTasksCard";
import ReadinessDial from "@/components/ReadinessDial";
import CollapsibleSection from "@/components/CollapsibleCard";

// Decomposed dashboard pieces (extracted Feb 2026 to keep this file lean)
import { TIME_OPTIONS, AOC_REMINDERS, formatHour, useSound } from "@/components/dashboard/utils";
import SmartTimeline from "@/components/dashboard/SmartTimeline";
import MeetingPlanner from "@/components/dashboard/MeetingPlanner";
import FocusTimer from "@/components/dashboard/FocusTimer";
import FlightPlan from "@/components/dashboard/FlightPlan";
import NewsWidget from "@/components/dashboard/NewsWidget";
import StatsCard from "@/components/dashboard/StatsCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [flightPlan, setFlightPlan] = useState(null);
  const [incidentSummary, setIncidentSummary] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isAOC, setIsAOC] = useState(false);
  const { playSound } = useSound();
  
  // Morning Briefing state
  const [showBriefing, setShowBriefing] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const { openBrainDump, isBrainDumpOpen, brainDumpShipCount } = useBrainDump();
  const [staleRefreshKey, setStaleRefreshKey] = useState(0);

  // Refresh stale tasks whenever the global brain dump closes OR whenever an
  // item is shipped from it — items may have been sent through and changed
  // task state.
  useEffect(() => {
    if (!isBrainDumpOpen) setStaleRefreshKey((k) => k + 1);
  }, [isBrainDumpOpen, brainDumpShipCount]);
  
  // Work hours state
  const [workHours, setWorkHours] = useState({
    start: 7.5,  // 7:30 AM default
    end: 16,     // 4:00 PM default
    lunchDuration: 0.5  // 30 min default
  });
  const [showWorkHoursDialog, setShowWorkHoursDialog] = useState(false);
  const [tempWorkHours, setTempWorkHours] = useState({ ...workHours });
  
  // Routine blocks - all OFF by default, user turns them on each day
  const [routineBlocks, setRoutineBlocks] = useState({
    incidentReview: false,
    rounds: false,
    lunch: false,
    shutdown: false
  });
  const [showMorningSetup, setShowMorningSetup] = useState(true);
  
  // Daily summary state
  const [dailySummary, setDailySummary] = useState(null);
  const [showRolloverBanner, setShowRolloverBanner] = useState(false);
  
  // Empowerment banner state
  const [empowerment, setEmpowerment] = useState(null);
  const [showEmpowerment, setShowEmpowerment] = useState(true);
  const [yesterdayCheckout, setYesterdayCheckout] = useState(null);
  
  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const today = now.toISOString().split('T')[0];
  const yesterdayStr = (() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  })();

  const fetchData = async () => {
    try {
      const [planRes, summaryRes, tasksRes, dailyRes, meetingsRes, empowermentRes, yCheckRes] = await Promise.all([
        axios.get(`${API}/flight-plans/today`, { withCredentials: true }),
        axios.get(`${API}/incidents/summary?days=1`, { withCredentials: true }),
        axios.get(`${API}/tasks?priority=Today`, { withCredentials: true }),
        axios.get(`${API}/daily-summary`, { withCredentials: true }),
        axios.get(`${API}/meetings?date=${today}`, { withCredentials: true }),
        axios.get(`${API}/empowerment/morning`, { withCredentials: true }),
        axios.get(`${API}/checkout/${yesterdayStr}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setFlightPlan(planRes.data);
      setIncidentSummary(summaryRes.data);
      setTodayTasks(tasksRes.data || []);
      setDailySummary(dailyRes.data);
      setMeetings(meetingsRes.data || []);
      setEmpowerment(empowermentRes.data);
      setYesterdayCheckout(yCheckRes.data);
      
      // Show rollover banner if there are incomplete tasks from yesterday
      if (dailyRes.data?.yesterday_incomplete?.length > 0) {
        setShowRolloverBanner(true);
      }
      
      // Check if empowerment was dismissed today
      const dismissedDate = sessionStorage.getItem("empowerment_dismissed_date");
      if (dismissedDate === today) {
        setShowEmpowerment(false);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const dismissEmpowerment = () => {
    setShowEmpowerment(false);
    sessionStorage.setItem("empowerment_dismissed_date", today);
  };

  const handleRollover = async () => {
    try {
      await axios.post(`${API}/tasks/rollover`, {}, { withCredentials: true });
      toast.success("Tasks rolled over to today!");
      setShowRolloverBanner(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Failed to rollover tasks");
    }
  };

  const addMeeting = async (meetingData) => {
    try {
      const response = await axios.post(`${API}/meetings`, {
        ...meetingData,
        date: today
      }, { withCredentials: true });
      setMeetings([...meetings, response.data]);
      toast.success("Meeting added!");
    } catch (error) {
      toast.error("Failed to add meeting");
    }
  };

  const deleteMeeting = async (meetingId) => {
    try {
      await axios.delete(`${API}/meetings/${meetingId}`, { withCredentials: true });
      setMeetings(meetings.filter(m => m.meeting_id !== meetingId));
      toast.success("Meeting removed");
    } catch (error) {
      toast.error("Failed to remove meeting");
    }
  };

  useEffect(() => {
    fetchData();
    // Load AOC status
    const savedAOC = sessionStorage.getItem("is_aoc");
    if (savedAOC) {
      setIsAOC(JSON.parse(savedAOC));
    }
    // Load work hours from localStorage (persists across sessions)
    const savedWorkHours = localStorage.getItem("work_hours");
    if (savedWorkHours) {
      const parsed = JSON.parse(savedWorkHours);
      setWorkHours(parsed);
      setTempWorkHours(parsed);
    }
    // Load today's routine blocks from sessionStorage (resets each day)
    const savedDate = sessionStorage.getItem("routine_date");
    const today = new Date().toISOString().split('T')[0];
    if (savedDate === today) {
      const savedRoutine = sessionStorage.getItem("routine_blocks");
      if (savedRoutine) {
        setRoutineBlocks(JSON.parse(savedRoutine));
        setShowMorningSetup(false);
      }
    } else {
      // New day - reset and show morning setup
      setShowMorningSetup(true);
      setRoutineBlocks({
        incidentReview: false,
        rounds: false,
        lunch: false,
        shutdown: false
      });
    }
    // Mount-only init: fetchData is stable in scope and we deliberately want
    // a single fetch on mount. `today`/`yesterdayStr` are recomputed every
    // render, so adding fetchData to deps would refire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save AOC status
  useEffect(() => {
    sessionStorage.setItem("is_aoc", JSON.stringify(isAOC));
  }, [isAOC]);

  // Save routine blocks for today
  const saveRoutineBlocks = (blocks) => {
    setRoutineBlocks(blocks);
    sessionStorage.setItem("routine_blocks", JSON.stringify(blocks));
    sessionStorage.setItem("routine_date", new Date().toISOString().split('T')[0]);
  };

  const completeMorningSetup = () => {
    saveRoutineBlocks(routineBlocks);
    setShowMorningSetup(false);
    toast.success("Day configured! Your schedule is ready.");
  };

  // Save work hours
  const saveWorkHours = () => {
    if (tempWorkHours.start >= tempWorkHours.end) {
      toast.error("End time must be after start time");
      return;
    }
    setWorkHours(tempWorkHours);
    localStorage.setItem("work_hours", JSON.stringify(tempWorkHours));
    setShowWorkHoursDialog(false);
    toast.success(`Schedule updated: ${formatHour(tempWorkHours.start)} - ${formatHour(tempWorkHours.end)}`);
  };

  const saveFlightPlan = async (data) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/flight-plans`, { date: today, ...data }, { withCredentials: true });
      toast.success("Flight plan saved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to save flight plan");
    }
  };

  const completedTasks = todayTasks.filter(t => t.completed).length;
  const totalTasks = todayTasks.length;

  return (
    <div data-testid="dashboard" className="space-y-6 animate-fade-in">
      {/* Header — stacks vertically on phone, side-by-side from md+. The
          action row wraps so 5+ buttons never force horizontal scroll. */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">
            Good {currentHour < 12 ? 'Morning' : currentHour < 17 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · <span className="text-purple-300/80">Director's Command Center</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Brain Dump Button — ⌘⇧D / Ctrl+⇧D global hotkey */}
          <Button
            onClick={openBrainDump}
            size="sm"
            className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-600/40 h-9"
            data-testid="brain-dump-open-btn"
            title="Brain Dump (Ctrl/⌘ + Shift + D)"
          >
            <Brain className="w-4 h-4 mr-2" />
            <span>Brain Dump</span>
            <kbd className="hidden md:inline-flex ml-2 items-center gap-0.5 text-[10px] font-mono px-1 py-0.5 rounded bg-black/30 border border-purple-400/30 text-purple-200/80">
              ⌘⇧D
            </kbd>
          </Button>

          {/* Morning Briefing Button — text label hidden on phone, icon only */}
          <Button
            onClick={() => setShowBriefing(true)}
            size="sm"
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 h-9"
            data-testid="morning-briefing-btn"
            title="Morning Briefing"
          >
            <Coffee className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Morning Briefing</span>
          </Button>

          {/* End of Day Checkout Button — text label hidden on phone, icon only */}
          <Button
            onClick={() => setShowCheckout(true)}
            size="sm"
            className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 h-9"
            data-testid="end-of-day-btn"
            title="End of Day Checkout"
          >
            <Moon className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">End of Day</span>
          </Button>

          {/* Streak Badge — shorter label on phone */}
          {dailySummary?.streak_days > 0 && (
            <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-medium text-orange-400">
                {dailySummary.streak_days}
                <span className="hidden sm:inline"> day streak</span>
              </span>
            </div>
          )}
          {/* AOC Toggle */}
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-full border transition-colors ${
              isAOC 
                ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' 
                : 'bg-zinc-800/50 border-zinc-700 text-muted-foreground'
            }`}>
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">AOC</span>
              <Switch
                checked={isAOC}
                onCheckedChange={setIsAOC}
                data-testid="aoc-toggle"
              />
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => setSoundEnabled(!soundEnabled)}
            data-testid="sound-toggle-btn"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Niko's Morning Brief — synthesis engine */}
      <CollapsibleSection id="dash:morning-brief" title="Niko's Morning Brief">
        <DailyBrief />
      </CollapsibleSection>

      {/* Stale Task Nudge — renders only when 3+ day rollovers exist */}
      <StaleTasksCard
        key={staleRefreshKey}
        onResolved={() => setStaleRefreshKey(k => k + 1)}
      />

      {/* Survey-Readiness Dial — live JC/CMS readiness with drill-down */}
      <CollapsibleSection id="dash:readiness" title="Survey Readiness">
        <ReadinessDial />
      </CollapsibleSection>

      {/* Today in 10 Seconds — glance bar */}
      {(() => {
        // Top priorities: high-priority tasks today
        const priorityTasks = todayTasks.filter(t => (t.priority || "").toLowerCase() === "high");
        const priorityDone = priorityTasks.filter(t => t.completed).length;
        const priorityTotal = priorityTasks.length;

        // Next meeting
        const upcoming = [...meetings]
          .map(m => ({ ...m, _s: m.start_time ?? m.start ?? 0 }))
          .filter(m => m._s > currentHour)
          .sort((a, b) => a._s - b._s);
        const next = upcoming[0];
        let nextLabel = "Clear runway";
        let nextSub = "";
        if (next) {
          const minutes = Math.round((next._s - currentHour) * 60);
          nextLabel = next.title || "Untitled meeting";
          nextSub = minutes <= 0 ? "starting now" : minutes < 60 ? `in ${minutes} min` : `in ${Math.floor(minutes / 60)}h ${minutes % 60}m`;
        }

        // Energy from yesterday's checkout
        const y = yesterdayCheckout;
        const energy = y?.energy_level;
        const mood = (y?.notes || "").match(/Mood:\s*(\w+)/)?.[1] || null;

        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="glance-bar">
            {/* Priorities chip */}
            <Card className="bento-card border-emerald-500/20 bg-emerald-500/5">
              <CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20 flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-semibold">Top Priorities</div>
                  <div className="text-xl font-bold text-white">
                    {priorityTotal > 0 ? `${priorityDone}/${priorityTotal}` : "Set them"}
                  </div>
                  <div className="text-xs text-zinc-400 truncate">
                    {priorityTotal === 0
                      ? "no high-priority tasks flagged"
                      : priorityDone === priorityTotal
                        ? "all done — protect the win"
                        : `${priorityTotal - priorityDone} left today`}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next meeting chip */}
            <Card className="bento-card border-blue-500/20 bg-blue-500/5">
              <CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20 flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-blue-300/80 font-semibold">Next Meeting</div>
                  <div className="text-xl font-bold text-white truncate" title={nextLabel}>{nextLabel}</div>
                  <div className="text-xs text-zinc-400 truncate">{nextSub || "nothing on the books"}</div>
                </div>
              </CardContent>
            </Card>

            {/* Energy from yesterday chip */}
            <Card className="bento-card border-purple-500/20 bg-purple-500/5">
              <CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20 flex-shrink-0">
                  <Zap className="w-5 h-5 text-purple-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-purple-300/80 font-semibold">Energy Yesterday</div>
                  <div className="text-xl font-bold text-white">
                    {energy != null ? (
                      <span>
                        {energy}<span className="text-zinc-500 text-base font-normal">/5</span>
                      </span>
                    ) : "No checkout"}
                  </div>
                  <div className="text-xs text-zinc-400 truncate capitalize">
                    {energy != null ? (mood ? `${mood} — adjust today's load` : "use End of Day to track trend") : "close out last night to calibrate"}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Tomorrow Look-Ahead — proactive burnout protection */}
      <CollapsibleSection id="dash:tomorrow" title="Tomorrow Look-Ahead">
        <TomorrowLookAhead />
      </CollapsibleSection>

      {/* Mid-day Checkpoint — only renders 11:30am–4pm local */}
      <CollapsibleSection id="dash:midday" title="Mid-day Checkpoint">
        <MiddayCheckpoint />
      </CollapsibleSection>

      {/* Morning Empowerment Banner */}
      {showEmpowerment && empowerment && (
        <Card className="bento-card border-primary/30 bg-gradient-to-r from-primary/10 via-violet-500/10 to-pink-500/10">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {/* Celebration Alert */}
                {empowerment.celebration && (
                  <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
                    <span className="text-lg">🎉</span>
                    <div>
                      <p className="text-sm font-semibold text-amber-300">{empowerment.celebration.name}</p>
                      <p className="text-xs text-amber-200/80">{empowerment.celebration.message}</p>
                    </div>
                  </div>
                )}
                
                {/* Quote */}
                <div className="mb-3">
                  <p className="text-lg italic text-foreground/90">
                    &ldquo;{empowerment.quote?.quote}&rdquo;
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    — {empowerment.quote?.author}
                  </p>
                </div>
                
                {/* Day greeting & month observances */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant="outline" className="bg-primary/10">
                    {empowerment.day_greeting}
                  </Badge>
                  {empowerment.month_observances?.map((obs, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className={
                        obs.type === 'mental_health' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                        obs.type === 'appreciation' ? 'bg-pink-500/10 text-pink-400 border-pink-500/30' :
                        'bg-zinc-500/10'
                      }
                    >
                      {obs.name}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={dismissEmpowerment}
                title="Dismiss until tomorrow"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Yesterday's Unfinished Banner */}
      {showRolloverBanner && dailySummary?.yesterday_incomplete?.length > 0 && (
        <Card className="bento-card border-violet-500/30 bg-violet-500/5">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                  <ArrowRight className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-violet-300">
                    {dailySummary.yesterday_incomplete.length} tasks from yesterday
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {dailySummary.yesterday_incomplete.slice(0, 3).map(t => t.description).join(', ')}
                    {dailySummary.yesterday_incomplete.length > 3 && '...'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowRolloverBanner(false)}
                >
                  Dismiss
                </Button>
                <Button 
                  size="sm" 
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={handleRollover}
                >
                  Roll Over to Today
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Morning Setup Banner - Shown at start of each day */}
      {showMorningSetup && (
        <Card className="bento-card border-primary/30 bg-primary/5">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-primary">
                  Good morning! Let&apos;s set up your day.
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Review your emails, add meetings, and toggle routines you need today.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[
                { key: 'incidentReview', label: 'Incident Review', icon: AlertTriangle, desc: '30 min morning review' },
                { key: 'rounds', label: 'Unit Rounds', icon: MapPin, desc: 'AM & PM rounds (25 min each)' },
                { key: 'lunch', label: 'Lunch Break', icon: Coffee, desc: 'Protected lunch time' },
                { key: 'shutdown', label: 'Shutdown', icon: CheckCircle2, desc: '30 min end-of-day wrap' },
              ].map((routine) => (
                <button
                  key={routine.key}
                  onClick={() => setRoutineBlocks({ ...routineBlocks, [routine.key]: !routineBlocks[routine.key] })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    routineBlocks[routine.key]
                      ? 'bg-primary/20 border-primary/50'
                      : 'bg-zinc-800/30 border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <routine.icon className={`w-4 h-4 ${routineBlocks[routine.key] ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-medium ${routineBlocks[routine.key] ? 'text-primary' : ''}`}>
                      {routine.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{routine.desc}</p>
                </button>
              ))}
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Add your meetings, then click ready to see your optimized schedule.
              </p>
              <Button onClick={completeMorningSetup} className="capture-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                I&apos;m Ready - Build My Day
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AOC Alert Banner */}
      {isAOC && (
        <Card className="bento-card border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-400 flex items-center gap-2">
                  Administrator On Call Today
                  <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">AOC</Badge>
                </h3>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {AOC_REMINDERS.map((reminder, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                        reminder.priority === 'high' 
                          ? 'bg-red-500/10 border border-red-500/20' 
                          : reminder.priority === 'medium'
                          ? 'bg-amber-500/10 border border-amber-500/20'
                          : 'bg-zinc-800/50 border border-zinc-700'
                      }`}
                    >
                      <Bell className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        reminder.priority === 'high' ? 'text-red-400' : 
                        reminder.priority === 'medium' ? 'text-amber-400' : 'text-zinc-400'
                      }`} />
                      <div>
                        <span className="text-xs text-muted-foreground">{reminder.time}:</span>
                        <p className="text-sm">{reminder.task}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Timeline */}
      <Card className="bento-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex items-center justify-between">
            <span className="flex items-center gap-2">
              Today&apos;s Schedule ({formatHour(workHours.start)} - {formatHour(workHours.end)})
              <Dialog open={showWorkHoursDialog} onOpenChange={setShowWorkHoursDialog}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 px-2" data-testid="edit-work-hours-btn">
                    <Settings className="w-3.5 h-3.5 mr-1" />
                    <span className="text-xs">Edit Hours</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Customize Work Hours
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                      Set your work hours for today. The schedule will adjust automatically.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Select 
                          value={tempWorkHours.start.toString()} 
                          onValueChange={(v) => setTempWorkHours({...tempWorkHours, start: parseFloat(v)})}
                        >
                          <SelectTrigger data-testid="work-start-time">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.filter(t => t.value >= 5 && t.value <= 14).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value.toString()}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Select 
                          value={tempWorkHours.end.toString()} 
                          onValueChange={(v) => setTempWorkHours({...tempWorkHours, end: parseFloat(v)})}
                        >
                          <SelectTrigger data-testid="work-end-time">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.filter(t => t.value >= 12 && t.value <= 22).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value.toString()}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Lunch Duration</Label>
                      <div className="flex gap-2">
                        {[
                          { label: "30 min", value: 0.5 },
                          { label: "45 min", value: 0.75 },
                          { label: "1 hour", value: 1 },
                        ].map((opt) => (
                          <Button
                            key={opt.value}
                            type="button"
                            size="sm"
                            variant={tempWorkHours.lunchDuration === opt.value ? "default" : "outline"}
                            onClick={() => setTempWorkHours({...tempWorkHours, lunchDuration: opt.value})}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="p-3 bg-zinc-800/50 rounded-lg text-sm">
                      <p className="text-muted-foreground">Preview:</p>
                      <p className="font-medium">
                        {formatHour(tempWorkHours.start)} - {formatHour(tempWorkHours.end)} 
                        <span className="text-muted-foreground ml-2">
                          ({((tempWorkHours.end - tempWorkHours.start) - tempWorkHours.lunchDuration).toFixed(1)} hrs work + {tempWorkHours.lunchDuration * 60}m lunch)
                        </span>
                      </p>
                    </div>
                    <Button onClick={saveWorkHours} className="w-full" data-testid="save-work-hours-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Save Work Hours
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              {/* Routine Toggles */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
                    <Settings className="w-3 h-3 mr-1" />
                    Routines
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Today&apos;s Routines</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {[
                      { key: 'incidentReview', label: 'Incident Review', icon: AlertTriangle, desc: '30 min morning review' },
                      { key: 'rounds', label: 'Unit Rounds', icon: MapPin, desc: 'AM & PM rounds (25 min each)' },
                      { key: 'lunch', label: 'Lunch Break', icon: Coffee, desc: 'Protected lunch time' },
                      { key: 'shutdown', label: 'Shutdown', icon: CheckCircle2, desc: '30 min end-of-day wrap' },
                    ].map((routine) => (
                      <div 
                        key={routine.key}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          routineBlocks[routine.key]
                            ? 'bg-primary/10 border-primary/30'
                            : 'bg-zinc-800/30 border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <routine.icon className={`w-4 h-4 ${routineBlocks[routine.key] ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div>
                            <p className={`text-sm font-medium ${routineBlocks[routine.key] ? 'text-primary' : ''}`}>
                              {routine.label}
                            </p>
                            <p className="text-xs text-muted-foreground">{routine.desc}</p>
                          </div>
                        </div>
                        <Switch
                          checked={routineBlocks[routine.key]}
                          onCheckedChange={(checked) => saveRoutineBlocks({ ...routineBlocks, [routine.key]: checked })}
                        />
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </span>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-blue-500/10 text-blue-400">Focus</Badge>
              <Badge variant="outline" className="bg-violet-500/10 text-violet-400">Meeting</Badge>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400">Rounds</Badge>
              <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400">Break</Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SmartTimeline meetings={meetings} currentTime={currentHour} workHours={workHours} routineBlocks={routineBlocks} />
          {/* Dynamic time labels based on work hours */}
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            {(() => {
              const labels = [];
              const totalHours = workHours.end - workHours.start;
              const step = totalHours / 5;
              for (let i = 0; i <= 5; i++) {
                const hour = workHours.start + (step * i);
                labels.push(
                  <span key={i}>{formatHour(hour)}</span>
                );
              }
              return labels;
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard
          title="Today's Tasks"
          value={`${completedTasks}/${totalTasks}`}
          subtitle="completed"
          icon={CheckCircle2}
          trend={completedTasks === totalTasks && totalTasks > 0 ? 'up' : undefined}
        />
        <StatsCard
          title="Meetings Today"
          value={meetings.length}
          subtitle={meetings.length > 0 ? `Next: ${meetings.sort((a,b) => a.start - b.start).find(m => m.start > currentHour)?.title || 'Done'}` : "None scheduled"}
          icon={Calendar}
        />
        <StatsCard
          title="Checkout Streak"
          value={dailySummary?.streak_days || 0}
          subtitle={(dailySummary?.streak_days || 0) > 0 ? "days in a row" : "close today to start"}
          icon={Flame}
          trend={(dailySummary?.streak_days || 0) >= 3 ? 'up' : undefined}
        />
        <StatsCard
          title="Focus Budget"
          value={workHours ? `${Math.max(0, workHours.end - workHours.start)}h` : "—"}
          subtitle="protected today"
          icon={Target}
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Flight Plan */}
        <div className="lg:col-span-2">
          <CollapsibleSection id="dash:flight-plan" title="Today's Four Bets">
            <FlightPlan plan={flightPlan} onSave={saveFlightPlan} />
          </CollapsibleSection>
        </div>

        {/* Meeting Planner */}
        <CollapsibleSection id="dash:meetings" title="Meetings">
          <MeetingPlanner 
            meetings={meetings} 
            setMeetings={setMeetings}
            onAddMeeting={addMeeting}
            onDeleteMeeting={deleteMeeting}
          />
        </CollapsibleSection>
      </div>

      {/* News Headlines Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <CollapsibleSection id="dash:news" title="Today's Headlines">
            <NewsWidget />
          </CollapsibleSection>
        </div>
        <CollapsibleSection id="dash:focus-timer" title="Focus Timer">
          <FocusTimer soundEnabled={soundEnabled} playSound={playSound} />
        </CollapsibleSection>
      </div>

      {/* Daily Leadership Wisdom */}
      <CollapsibleSection id="dash:wisdom" title="Daily Leadership Wisdom">
        <DailyWisdomCard />
      </CollapsibleSection>

      {/* Today's Tasks Row */}
      <div className="grid grid-cols-1 gap-4">
        
        {/* Today's Tasks Preview */}
        <Card className="bento-card lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Today&apos;s Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No tasks for today. Use Quick Capture to add tasks
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {todayTasks.slice(0, 6).map((task) => (
                  <div 
                    key={task.task_id}
                    className={`flex items-center gap-3 p-2 rounded-lg ${task.completed ? 'opacity-50' : 'bg-zinc-800/30'}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      task.category === 'Safety' ? 'bg-red-500' :
                      task.category === 'Staff' ? 'bg-blue-500' :
                      task.category === 'Admin' ? 'bg-amber-500' :
                      'bg-emerald-500'
                    }`} />
                    <span className={`flex-1 text-sm ${task.completed ? 'line-through' : ''}`}>
                      {task.description}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Morning Briefing Modal */}
      <MorningBriefing 
        open={showBriefing} 
        onClose={() => setShowBriefing(false)}
      />

      {/* End of Day Conversational Checkout */}
      <EndOfDayCheckout
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
      />
    </div>
  );
}
